import type { DomainTopic, TopicTier } from '../services/page-comparison';

/** Mirrors page-comparison Topic Fit card state (no component coupling). */
export interface QuickCompareTopicFitData {
  mainTopicSlug: string;
  mainTopicLabel: string;
  hasData: boolean;
  topicFound: boolean;
  tier: TopicTier | null;
  authorityScore: number | null;
  loading: boolean;
  error: string | null;
}

export function emptyTopicFitData(): QuickCompareTopicFitData {
  return {
    mainTopicSlug: '',
    mainTopicLabel: '',
    hasData: false,
    topicFound: false,
    tier: null,
    authorityScore: null,
    loading: false,
    error: null
  };
}

export function extractDomainFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function getTopicSignalsSorted(analysis: any): any[] {
  const signals = analysis?.topic?.topicSignals || [];
  return [...signals].sort((a, b) => (b.weight || 0) - (a.weight || 0));
}

export function getTopicSignalsCount(analysis: any): number {
  return getTopicSignalsSorted(analysis).length;
}

export function getMainTopicFromAnalysis(
  analysis: any
): { topicSlug: string; label: string; weight: number } | null {
  const signals = getTopicSignalsSorted(analysis);
  if (signals.length === 0) return null;
  const top = signals[0];
  return {
    topicSlug: top.topicSlug || '',
    label: top.label || top.topicSlug || '',
    weight: top.weight || 0
  };
}

export function computeTopicFitData(args: {
  mainTopic: { topicSlug: string; label: string; weight: number } | null;
  parentTopics: DomainTopic[] | undefined;
  domainTopicsLoading: boolean;
  domainTopicsError: string | null;
}): QuickCompareTopicFitData {
  const empty = emptyTopicFitData();
  const { mainTopic, parentTopics, domainTopicsLoading, domainTopicsError } = args;

  if (!mainTopic || !mainTopic.topicSlug) {
    return empty;
  }

  const base = {
    mainTopicSlug: mainTopic.topicSlug,
    mainTopicLabel: mainTopic.label
  };

  if (domainTopicsLoading) {
    return { ...empty, ...base, loading: true };
  }

  if (domainTopicsError) {
    return { ...empty, ...base, error: domainTopicsError };
  }

  if (parentTopics === undefined) {
    return { ...empty, ...base, loading: true };
  }

  if (parentTopics.length === 0) {
    return { ...empty, ...base, hasData: false };
  }

  const normalizedMainTopic = mainTopic.topicSlug.toLowerCase().trim();
  const matchingTopic = parentTopics.find((t) => {
    if (t.parentTopicSlug.toLowerCase().trim() === normalizedMainTopic) {
      return true;
    }
    if (
      t.childTopicSlugs?.some((child) => child.toLowerCase().trim() === normalizedMainTopic)
    ) {
      return true;
    }
    return false;
  });

  if (!matchingTopic) {
    return {
      mainTopicSlug: mainTopic.topicSlug,
      mainTopicLabel: mainTopic.label,
      hasData: true,
      topicFound: false,
      tier: null,
      authorityScore: null,
      loading: false,
      error: null
    };
  }

  return {
    mainTopicSlug: mainTopic.topicSlug,
    mainTopicLabel: matchingTopic.label || mainTopic.label,
    hasData: true,
    topicFound: true,
    tier: matchingTopic.topicTier || null,
    authorityScore: matchingTopic.authorityScore,
    loading: false,
    error: null
  };
}

export function formatTopicTier(tier: TopicTier | null): string {
  if (!tier) return 'Unknown';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function getTopicTierClass(tier: TopicTier | null): string {
  if (!tier) return 'tier-unknown';
  return `tier-${tier}`;
}

/**
 * The same six tiers as `.badge` tones, for the screens whose stylesheets have
 * migrated. getTopicTierClass() above stays because page-comparison still
 * paints its own .tier-* rules from those names.
 *
 * `owned` is the only good news and `anti` the only bad one. `unclassified` is
 * an absence of a verdict rather than a verdict, so it is --muted rather than
 * a colour.
 *
 * `aspirational` and `adjacent` are both "not yours yet". They take --accent
 * and --info, which is the honest reading of each, but it does NOT separate
 * them on screen: --info-text/-surface/-border are aliases of the accent
 * three in this palette, so both render the same blue. They rendered the same
 * blue before this too -- the label is what tells them apart, and a second
 * cool tone is what the component layer would need to fix it.
 */
export function topicTierBadgeClass(tier: TopicTier | null): string {
  switch (tier) {
    case 'owned':
      return 'badge badge--pill badge--success';
    case 'aspirational':
      return 'badge badge--pill badge--accent';
    case 'adjacent':
      return 'badge badge--pill badge--info';
    case 'anti':
      return 'badge badge--pill badge--danger';
    case 'ambient':
      return 'badge badge--pill';
    default:
      return 'badge badge--pill badge--muted';
  }
}

export function getLinkTypeLabel(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'Contextual';
    case 'structural':
      return 'Structural';
    case 'same_page':
      return 'Same-page';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

export function getLinkTypeTooltip(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'In-content link that appears on a small subset of pages.';
    case 'structural':
      return 'Navigation/footer/sidebar link that appears across many pages.';
    case 'same_page':
      return 'Anchor link within the same page (table of contents or section jump).';
    case 'unknown':
    default:
      return 'Not enough signals to classify this link yet.';
  }
}

export function getLinkTypeClass(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'link-type-contextual';
    case 'structural':
      return 'link-type-structural';
    case 'same_page':
      return 'link-type-same-page';
    case 'unknown':
    default:
      return 'link-type-unknown';
  }
}

/**
 * The same four link types as `.badge` tones. getLinkTypeClass() above stays
 * for url-detail and page-comparison, which still paint their own
 * .link-type-* rules.
 *
 * `structural` and `same_page` are both blue and stay both blue: --info is an
 * alias of --accent in this palette. Their old rules were the same blue as
 * each other as well (--text-link and --accent-text are one token), so the
 * label carries the difference here exactly as it did before.
 */
export function linkTypeBadgeClass(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'badge badge--pill badge--success';
    case 'structural':
      return 'badge badge--pill badge--accent';
    case 'same_page':
      return 'badge badge--pill badge--info';
    case 'unknown':
    default:
      return 'badge badge--pill';
  }
}

export function formatAnchorText(anchorText: string | undefined): string {
  if (!anchorText || anchorText.trim().length === 0) {
    return '—';
  }
  return anchorText;
}

export function linkTargetDisplayUrl(analysis: any): string {
  return (
    analysis?.rawData?.pageData?.url || analysis?.rawData?.summary?.url || 'Current page'
  );
}

export function resolveUrlIdFromAnalysis(analysis: any): string | null {
  if (!analysis?.urlId) return null;
  if (typeof analysis.urlId === 'string') return analysis.urlId;
  const oid = analysis.urlId as any;
  return oid?._id?.toString() || oid?.toString() || null;
}
